import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

export const metricsApi = {
  snapshot: () => api.get<MetricsSnapshot>('/api/admin/metrics/snapshot').then((r) => r.data),
  // POST (not GET) because PromQL queries contain `{…}` and those characters
  // trip Netty/Traefik URL validators even when percent-encoded. Body = safe.
  history: (metric: string, window: string = '1h') =>
    api.post<MetricPoint[]>('/api/admin/metrics/history', { metric, window }).then((r) => r.data),
};

export interface MetricsSnapshot {
  // JVM
  heapUsedMb: number;
  heapMaxMb: number;
  nonHeapUsedMb: number;
  cpuUsage: number;
  systemCpuUsage: number;
  threadCount: number;
  gcPauseP95Ms: number;
  uptimeSeconds: number;
  startTimeEpoch: number;
  // HTTP
  httpRequestsTotal: number;
  httpAvgLatencyMs: number;
  httpLatencyP50Ms: number;
  httpLatencyP95Ms: number;
  httpLatencyP99Ms: number;
  httpErrorRate: number;
  // DB pool
  dbPoolActive: number;
  dbPoolIdle: number;
  dbPoolMax: number;
  // Business counters
  postsGeneratedTotal: number;
  postsPublishedTotal: number;
  postsPublishFailedTotal: number;
  aiCallsTotal: number;
  aiTokensInputTotal: number;
  aiTokensOutputTotal: number;
  syncRunsTotal: number;
  syncPostsEditedTotal: number;
  syncPostsDeletedTotal: number;
  // Business gauges (refreshed every 60s)
  activeWorkspaces: number;
  postsPendingReview: number;
  postsPublishedToday: number;
  postsScheduledUpcoming: number;
  connectionsActive: number;
  connectionsHealthy: number;
  // Meta
  prometheusAvailable: boolean;
  snapshotAt: string;
}

export interface MetricPoint {
  timestamp: number;
  value: number;
}

// ── Discover (trending topics + content ideas) ──────────────────────────

export interface TrendingTopicsResponse {
  brands: BrandTrendingData[];
  lastRefreshedAt: string | null;
  isStale: boolean;
}

export interface BrandTrendingData {
  brandId: string;
  brandName: string;
  industryContext: string | null;
  trendingHashtags: TrendingHashtagItem[];
  contentIdeas: ContentIdeaItem[];
  refreshedAt: string | null;
}

export interface TrendingHashtagItem {
  tag: string;
  relevanceScore: number;
  trend: 'up' | 'down' | 'stable';
  source?: string;
  sourceUrl?: string;
}

export interface ContentIdeaItem {
  title: string;
  angle: string;
  contentType: string;
  why: string;
  source?: string;
  sourceUrl?: string;
}

// ── Storylines ──────────────────────────────────────────────────────────

export interface StorylineResponse {
  id: string;
  workspaceId: string;
  brandId: string;
  brandName?: string;
  name: string;
  goal?: string;
  targetDate: string;
  status: string;
  platforms: string[];
  createdAt?: string;
  updatedAt?: string;
  posts?: StorylinePostResponse[];
  arcStubs?: ArcStubResponseItem[];
  totalPosts: number;
  publishedPosts: number;
  daysUntilTarget: number;
}

export interface StorylinePostResponse {
  id: string;
  position?: number;
  scheduledAt?: string;
  arcBeat?: string;
  contentType?: string;
  content?: string;
  status: string;
  reviewStatus?: string;
  platform: string;
  language?: string;
  uploadMediaUrl?: string;
  uploadMediaType?: string;
}

export interface ArcStubResponseItem {
  position: number;
  suggestedDate: string;
  arcBeat: string;
  contentType: string;
  titleHint: string;
  copyStub?: string;
  copyStubNl?: string;
  copyStubEn?: string;
  mediaHint: string;
}
